import React, { useState, useEffect } from 'react'
import { 
  Search, User, FileText, CreditCard, TrendingUp, AlertCircle, CheckCircle, 
  X, DollarSign, Building2, Clock, CheckCheck, Users, BarChart3, 
  Activity, RefreshCw, ChevronRight, PieChart as PieChartIcon, LogOut, Menu, ChevronLeft,
  ArrowLeftRight, Shield, ShieldCheck, ShieldAlert, AlertTriangle, Eye, Truck, Package,
  RotateCcw, Hash, ShoppingCart, Store, Globe
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, AreaChart, Area, ResponsiveContainer
} from 'recharts'
import { apiService } from '../../services/apiService'
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext'

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16']

const SuperAdminDashboard = () => {
  const { superAdmin, logout } = useSuperAdminAuth()
  
  // Navigation
  const [activeView, setActiveView] = useState('dashboard')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  
  // Dashboard stats
  const [dashboardLoading, setDashboardLoading] = useState(true)
  const [tenantData, setTenantData] = useState([])
  const [globalStats, setGlobalStats] = useState({
    totalMGs: 0, totalCustomers: 0, activeMGs: 0, inaccessibleMGs: 0
  })
  
  // Member Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [memberData, setMemberData] = useState(null)
  const [financialStatements, setFinancialStatements] = useState([])
  const [loans, setLoans] = useState([])
  const [shares, setShares] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState('overview')
  
  // UFCC Commission state
  const [ufccCommissions, setUfccCommissions] = useState(null)
  const [ufccLoading, setUfccLoading] = useState(true)
  const [ufccError, setUfccError] = useState('')
  const [confirmingSettlement, setConfirmingSettlement] = useState(null)
  const [confirmationRef, setConfirmationRef] = useState('')
  const [confirmationNotes, setConfirmationNotes] = useState('')

  // Cross-MG Clearing House state
  const [clearingData, setClearingData] = useState(null)
  const [allBatches, setAllBatches] = useState([])
  const [clearingLoading, setClearingLoading] = useState(false)
  const [clearingError, setClearingError] = useState('')
  const [clearingFilter, setClearingFilter] = useState('all')
  const [approvingBatch, setApprovingBatch] = useState(null)
  const [approvalNotes, setApprovalNotes] = useState('')
  const [disputes, setDisputes] = useState([])

  // Complaints & Refunds state
  const [complaintsLoaded, setComplaintsLoaded] = useState(false)
  const [complaintsLoading, setComplaintsLoading] = useState(false)
  const [complaintError, setComplaintError] = useState('')
  const [complaintSuccess, setComplaintSuccess] = useState('')
  const [allComplaints, setAllComplaints] = useState([])
  const [complaintsFilter, setComplaintsFilter] = useState('all')
  const [refundSearch, setRefundSearch] = useState('')
  const [refundLookupLoading, setRefundLookupLoading] = useState(false)
  const [refundOrder, setRefundOrder] = useState(null)
  const [refundReason, setRefundReason] = useState('')
  const [processingRefund, setProcessingRefund] = useState(false)
  const [resolvingDispute, setResolvingDispute] = useState(null)
  const [resolveResolution, setResolveResolution] = useState('resolved_buyer')
  const [resolveNotes, setResolveNotes] = useState('')

  // Marketplace Stats state
  const [marketplaceStats, setMarketplaceStats] = useState(null)
  const [marketplaceLoading, setMarketplaceLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
    fetchUfccCommissions()
    fetchClearingData()
    fetchMarketplaceStats()
  }, [])

  useEffect(() => {
    if (activeView === 'complaints-refunds' && !complaintsLoaded) {
      fetchComplaints()
    }
  }, [activeView])

  const fetchDashboardData = async () => {
    setDashboardLoading(true)
    try {
      const tenantResponse = await apiService.getRegisteredActiveTenants().catch(() => ({ status: 'ERROR', tenants: [] }))
      const tenants = tenantResponse?.tenants || []
      setTenantData(tenants)

      const activeTenants = tenants.filter(t => t.status === 'active')
      const inaccessibleTenants = tenants.filter(t => t.status === 'inaccessible')
      const totalCustomers = tenants.reduce((sum, t) => sum + (t.customerCount || 0), 0)

      setGlobalStats({
        totalMGs: tenants.length,
        totalCustomers,
        activeMGs: activeTenants.length,
        inaccessibleMGs: inaccessibleTenants.length
      })
    } catch (err) {
      console.error('Dashboard data fetch error:', err)
    } finally {
      setDashboardLoading(false)
    }
  }

  const fetchUfccCommissions = async () => {
    setUfccLoading(true)
    setUfccError('')
    try {
      // Fetch all-time commissions (wide date range)
      const response = await apiService.getKataleAllUfccCommissions('2024-01-01', null)
      if (response.status === 'SUCCESS' && response.data) {
        setUfccCommissions(response.data)
      } else {
        setUfccError('Failed to load UFCC commission data')
      }
    } catch (err) {
      console.error('UFCC fetch error:', err)
      setUfccError('Failed to load UFCC commission data')
    } finally {
      setUfccLoading(false)
    }
  }

  const handleConfirmSettlement = async (settlement) => {
    if (!confirmationRef.trim()) { alert('Please enter a confirmation reference'); return }
    try {
      const response = await apiService.confirmKataleUfccSettlementReceipt(settlement.id, confirmationRef, confirmationNotes)
      if (response.status === 'SUCCESS') {
        setConfirmingSettlement(null)
        setConfirmationRef('')
        setConfirmationNotes('')
        fetchUfccCommissions()
      } else { alert(response.message || 'Failed to confirm settlement') }
    } catch (err) { console.error('Confirm settlement error:', err); alert('Failed to confirm settlement') }
  }

  // Cross-MG Clearing House
  const fetchClearingData = async () => {
    setClearingLoading(true)
    setClearingError('')
    try {
      const [summaryRes, batchesRes] = await Promise.all([
        apiService.getKataleClearingSummary(),
        apiService.getKataleAllSettlementBatches()
      ])
      if (summaryRes.status === 'SUCCESS') setClearingData(summaryRes.data)
      if (batchesRes.status === 'SUCCESS') setAllBatches(batchesRes.data || [])
      
      // Try to get disputes
      try {
        const disputeRes = await apiService.makeAuthenticatedRequest('KATALE-GET-ALL-DISPUTES', {}, false, true)
        if (disputeRes.status === 'SUCCESS') setDisputes(disputeRes.data || [])
      } catch (e) { console.log('Disputes not yet available:', e) }
    } catch (err) {
      console.error('Clearing data error:', err)
      setClearingError('Failed to load clearing house data')
    } finally {
      setClearingLoading(false)
    }
  }

  const fetchMarketplaceStats = async () => {
    setMarketplaceLoading(true)
    try {
      const response = await apiService.getKataleMarketplaceStats()
      if (response.status === 'SUCCESS' && response.data) {
        setMarketplaceStats(response.data)
      }
    } catch (err) {
      console.error('Marketplace stats fetch error:', err)
    } finally {
      setMarketplaceLoading(false)
    }
  }

  const handleApproveBatch = async (batch) => {
    try {
      const response = await apiService.approveKataleCrossMgSettlement(batch.id, approvalNotes, confirmationRef)
      if (response.status === 'SUCCESS') {
        setApprovingBatch(null)
        setApprovalNotes('')
        setConfirmationRef('')
        fetchClearingData()
        alert('Settlement confirmed and settled successfully')
      } else { alert(response.message || 'Failed to confirm settlement') }
    } catch (err) { alert('Error: ' + err.message) }
  }

  const searchMember = async () => {
    if (!searchQuery.trim()) { setError('Please enter a member number'); return }
    setLoading(true); setError(''); setSuccess(''); setMemberData(null); setSearchResults([])
    setFinancialStatements([]); setLoans([]); setShares(null)

    try {
      const searchResponse = await apiService.searchCustomersGlobal(searchQuery.trim())
      const customers = Array.isArray(searchResponse.data) ? searchResponse.data : searchResponse.data?.customers || []

      if (!searchResponse.success || customers.length === 0) {
        setError('Member not found. Please check the member number and try again.')
        return
      }
      if (customers.length > 1) {
        setSearchResults(customers)
        setSuccess(`Found ${customers.length} members matching '${searchQuery.trim()}'. Please select one.`)
        return
      }
      await selectMember(customers[0])
    } catch (err) {
      console.error('Search error:', err)
      setError('Failed to search for member. Please try again.')
    } finally { setLoading(false) }
  }

  const selectMember = async (member) => {
    setLoading(true); setSearchResults([])
    const normalizedMember = {
      ...member,
      memberNo: member.memberNo || member.memberNumber,
      tenantDb: member.tenantDb || member.tenantDatabase,
      tenantName: member.tenantName || member.mgCode
    }
    setMemberData(normalizedMember)
    setSuccess(`Member found: ${normalizedMember.fullName} (${normalizedMember.memberNo}) from ${normalizedMember.tenantName}`)

    try {
      // Pass tenantDb to all fetch calls to ensure correct MG database is queried
      const tenantDb = normalizedMember.tenantDb
      await Promise.all([
        fetchFinancialStatements(normalizedMember.memberNo, tenantDb),
        fetchLoans(normalizedMember.customerId, tenantDb),
        fetchShares(normalizedMember.customerId, tenantDb)
      ])
    } catch (err) { console.error('Error fetching member data:', err) } 
    finally { setLoading(false) }
  }

  const fetchFinancialStatements = async (memberNo, tenantDb = null) => {
    try {
      const r = await apiService.getCustomerFinancialStatementsByMemberNo(memberNo, tenantDb)
      if (r.success && r.data) setFinancialStatements(r.data)
    } catch (e) { console.error('Failed to fetch financial statements:', e) }
  }

  const fetchLoans = async (customerId, tenantDb = null) => {
    try {
      const r = await apiService.getCustomerLoansByCustomerId(customerId, tenantDb)
      setLoans(r.success && r.data?.loanApplications ? r.data.loanApplications : [])
    } catch (e) { console.error('Failed to fetch loans:', e); setLoans([]) }
  }

  const fetchShares = async (customerId, tenantDb = null) => {
    try {
      const r = await apiService.getCustomerShares(customerId, tenantDb)
      if (r.success && r.data) setShares(r.data)
    } catch (e) { console.error('Failed to fetch shares:', e) }
  }

  const handleKeyPress = (e) => { if (e.key === 'Enter') searchMember() }

  const clearSearch = () => {
    setSearchQuery(''); setSearchResults([]); setMemberData(null)
    setFinancialStatements([]); setLoans([]); setShares(null)
    setError(''); setSuccess(''); setActiveTab('overview')
  }

  const formatCurrency = (amount) => new Intl.NumberFormat('en-UG', {
    style: 'currency', currency: 'UGX', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount || 0)

  const formatShort = (amount) => {
    const n = Number(amount) || 0
    if (n >= 1e9) return `UGX ${(n / 1e9).toFixed(1)}B`
    if (n >= 1e6) return `UGX ${(n / 1e6).toFixed(1)}M`
    if (n >= 1e3) return `UGX ${(n / 1e3).toFixed(0)}K`
    return formatCurrency(n)
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-UG', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'
  const fmtMg = (n) => n ? n.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/ Mg/g, ' MG') : 'Unknown'

  // ===== Complaints & Refunds functions =====
  const fetchComplaints = async () => {
    setComplaintsLoading(true)
    setComplaintsLoaded(true)
    setComplaintError('')
    try {
      const res = await apiService.makeAuthenticatedRequest('KATALE-GET-ALL-DISPUTES', {}, false, true)
      const list = res.data?.disputes || res.data || []
      setAllComplaints(Array.isArray(list) ? list : [])
    } catch (err) {
      console.error('Fetch complaints error:', err)
      setComplaintError('Failed to load complaints')
    } finally {
      setComplaintsLoading(false)
    }
  }

  const lookupOrderForRefund = async () => {
    if (!refundSearch.trim()) return
    setRefundLookupLoading(true)
    setComplaintError('')
    setRefundOrder(null)
    try {
      const res = await apiService.makeAuthenticatedRequest('KATALE-LOOKUP-ORDER-BY-REF', { reference: refundSearch.trim() }, false, true)
      if (res.status === 'SUCCESS' && res.data) {
        setRefundOrder(res.data)
      } else {
        setComplaintError(res.message || 'Order not found')
      }
    } catch (err) {
      setComplaintError('Lookup failed: ' + err.message)
    } finally {
      setRefundLookupLoading(false)
    }
  }

  const handleProcessRefund = async () => {
    if (!refundOrder || !refundReason.trim()) return
    setProcessingRefund(true)
    setComplaintError('')
    setComplaintSuccess('')
    try {
      const res = await apiService.makeAuthenticatedRequest('KATALE-PROCESS-REFUND', {
        order_id: refundOrder.order_id,
        reason: refundReason.trim()
      }, false, true)
      if (res.status === 'SUCCESS') {
        setComplaintSuccess(res.message || 'Refund processed successfully')
        setRefundOrder(null)
        setRefundSearch('')
        setRefundReason('')
        fetchComplaints()
      } else {
        setComplaintError(res.message || 'Refund failed')
      }
    } catch (err) {
      setComplaintError('Refund failed: ' + err.message)
    } finally {
      setProcessingRefund(false)
    }
  }

  const handleResolveDispute = async () => {
    if (!resolvingDispute || !resolveNotes.trim()) return
    try {
      const res = await apiService.makeAuthenticatedRequest('KATALE-RESOLVE-DISPUTE', {
        dispute_id: resolvingDispute.id,
        resolution: resolveResolution,
        notes: resolveNotes.trim()
      }, false, true)
      if (res.status === 'SUCCESS') {
        setComplaintSuccess('Dispute resolved successfully')
        setResolvingDispute(null)
        setResolveNotes('')
        fetchComplaints()
      } else {
        setComplaintError(res.message || 'Failed to resolve dispute')
      }
    } catch (err) {
      setComplaintError('Failed: ' + err.message)
    }
  }

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { key: 'katale-management', label: 'Katale Management', icon: ShoppingCart, badge: marketplaceStats?.disputes?.open_disputes > 0 ? `${marketplaceStats.disputes.open_disputes}` : null, badgeColor: 'warning' },
    { key: 'member-search', label: 'Member Search', icon: Search },
    { key: 'clearing-house', label: 'Clearing House', icon: ArrowLeftRight,
      badge: allBatches.filter(b => b.status === 'pending' || b.status === 'awaiting_confirmation').length > 0 ? `${allBatches.filter(b => b.status === 'pending' || b.status === 'awaiting_confirmation').length}` : null,
      badgeColor: 'warning' },
    { key: 'ufcc-commissions', label: 'Commissions', icon: DollarSign,
      badge: ufccCommissions?.summary?.mgs_awaiting_confirmation > 0 ? `${ufccCommissions.summary.mgs_awaiting_confirmation} pending` : null,
      badgeColor: 'warning' },
    { key: 'complaints-refunds', label: 'Complaints & Refunds', icon: AlertTriangle,
      badge: allComplaints.filter(c => c.status === 'open' || c.status === 'investigating').length > 0 ? `${allComplaints.filter(c => c.status === 'open' || c.status === 'investigating').length}` : null,
      badgeColor: 'warning' },
  ]
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl p-6 md:p-8 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold">
              Welcome back, {superAdmin?.fullName || superAdmin?.full_name || 'Super Admin'}
            </h2>
            <p className="mt-2 text-blue-100 text-sm md:text-base">
              UFCC Federation Overview — {new Date().toLocaleDateString('en-UG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <button onClick={() => { fetchDashboardData(); fetchUfccCommissions(); fetchMarketplaceStats() }} 
            className="mt-4 md:mt-0 flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur px-4 py-2 rounded-lg text-sm font-medium transition">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      {dashboardLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-3" /><div className="h-8 bg-gray-200 rounded w-16" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard icon={Building2} label="Total MGs" value={globalStats.totalMGs} sub={`${globalStats.activeMGs} active`} color="blue" onClick={() => setActiveView('mg-overview')} />
          <StatCard icon={Users} label="Total Members" value={globalStats.totalCustomers.toLocaleString()} sub="Across all MGs" color="emerald" />
          <StatCard icon={DollarSign} label="Commission Earned" value={formatCurrency(ufccCommissions?.summary?.total_commission)} sub={ufccLoading ? 'Loading...' : 'MG Katale (All-time)'} color="amber" onClick={() => setActiveView('ufcc-commissions')} />
          <StatCard icon={Clock} label="Pending Settlements" value={ufccCommissions?.pending_settlements?.length || 0} sub={ufccCommissions?.summary?.total_pending ? formatCurrency(ufccCommissions.summary.total_pending) : 'UGX 0'} color="rose" onClick={() => setActiveView('ufcc-commissions')} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" /> Quick Actions
          </h3>
          <div className="space-y-3">
            <QuickAction icon={Search} label="Search Member Globally" desc="Find any member across all MGs" onClick={() => setActiveView('member-search')} />
            <QuickAction icon={ShoppingCart} label="Katale Management" desc={`${marketplaceStats?.orders?.total_orders || 0} total orders`} onClick={() => setActiveView('katale-management')} />
            <QuickAction icon={DollarSign} label="Review Commissions"
              desc={ufccCommissions?.pending_settlements?.length > 0 ? `${ufccCommissions.pending_settlements.length} settlements pending` : 'All settlements confirmed'}
              onClick={() => setActiveView('ufcc-commissions')}
              highlight={ufccCommissions?.pending_settlements?.length > 0} />
          </div>
        </div>

        {/* MG Bar Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" /> MG Member Distribution
            </h3>
            <button onClick={() => setActiveView('katale-management')} className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
              Katale Dashboard <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {dashboardLoading ? (
            <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={tenantData.filter(t => t.status === 'active' && t.customerCount > 0)
                .sort((a, b) => (b.customerCount || 0) - (a.customerCount || 0))
                .slice(0, 10)
                .map(t => ({ name: fmtMg(t.name).replace(/ MG$/i, ''), members: t.customerCount || 0 }))}
                margin={{ top: 5, right: 20, left: 5, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => [value.toLocaleString(), 'Members']} />
                <Bar dataKey="members" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Pending Alert */}
      {!ufccLoading && ufccCommissions?.pending_settlements?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 rounded-lg"><Clock className="h-5 w-5 text-amber-600" /></div>
            <div className="flex-1">
              <h4 className="font-semibold text-amber-900">
                {ufccCommissions.pending_settlements.length} Settlement{ufccCommissions.pending_settlements.length > 1 ? 's' : ''} Awaiting Confirmation
              </h4>
              <p className="text-sm text-amber-700 mt-1">
                Total: <strong>{formatCurrency(ufccCommissions.pending_settlements.reduce((s, p) => s + (p.amount || 0), 0))}</strong>
              </p>
              <button onClick={() => setActiveView('ufcc-commissions')}
                className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
                Review Settlements <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Commission Breakdown + Katale Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {!ufccLoading && ufccCommissions && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PieChartIcon className="h-5 w-5 text-blue-600" /> Commission Breakdown
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Confirmed', value: Number(ufccCommissions.summary?.total_confirmed) || 0 },
                    { name: 'Settled (Awaiting)', value: Number(ufccCommissions.summary?.total_settled) || 0 },
                    { name: 'Pending', value: Number(ufccCommissions.summary?.total_pending) || 0 }
                  ].filter(d => d.value > 0)}
                  cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {['#10B981', '#06B6D4', '#F59E0B'].map((color, i) => (
                    <Cell key={i} fill={color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <MiniStat label="Confirmed" value={formatShort(ufccCommissions.summary?.total_confirmed)} bg="emerald" />
              <MiniStat label="Settled" value={formatShort(ufccCommissions.summary?.total_settled)} bg="cyan" />
              <MiniStat label="Pending" value={formatShort(ufccCommissions.summary?.total_pending)} bg="amber" />
            </div>
          </div>
        )}

        {/* Katale Marketplace Quick Stats */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-emerald-600" /> Katale Marketplace
          </h3>
          {marketplaceLoading ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}</div>
          ) : marketplaceStats ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <MiniStat label="Total Orders" value={marketplaceStats.orders?.total_orders || 0} bg="blue" />
                <MiniStat label="GMV" value={formatShort(marketplaceStats.orders?.total_gmv)} bg="emerald" />
                <MiniStat label="Active Products" value={marketplaceStats.products?.active_products || 0} bg="cyan" />
                <MiniStat label="Active Sellers" value={marketplaceStats.products?.total_sellers || 0} bg="amber" />
              </div>
              {(marketplaceStats.disputes?.open_disputes > 0 || marketplaceStats.orders?.escrow_disputed > 0) && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  <AlertTriangle className="h-4 w-4" />
                  {marketplaceStats.disputes?.open_disputes || 0} open disputes · {marketplaceStats.orders?.escrow_disputed || 0} escrow disputed
                </div>
              )}
              <button onClick={() => setActiveView('katale-management')} 
                className="w-full text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center gap-1 py-2 bg-blue-50 rounded-lg hover:bg-blue-100 transition">
                View Katale Dashboard <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No marketplace data available</p>
          )}
        </div>
      </div>
    </div>
  )

  // ==================== KATALE MANAGEMENT ====================
  const renderKataleManagement = () => {
    const stats = marketplaceStats
    const orderStatusData = stats ? [
      { name: 'Completed', value: Number(stats.orders?.completed_orders) || 0 },
      { name: 'Pending', value: Number(stats.orders?.pending_orders) || 0 },
      { name: 'Processing', value: Number(stats.orders?.processing_orders) || 0 },
      { name: 'Cancelled', value: Number(stats.orders?.cancelled_orders) || 0 }
    ].filter(d => d.value > 0) : []

    const escrowData = stats ? [
      { name: 'Held', value: Number(stats.orders?.escrow_held) || 0 },
      { name: 'Released', value: Number(stats.orders?.escrow_released) || 0 },
      { name: 'Disputed', value: Number(stats.orders?.escrow_disputed) || 0 },
      { name: 'Refunded', value: Number(stats.orders?.escrow_refunded) || 0 }
    ].filter(d => d.value > 0) : []

    const monthlyData = stats?.monthly_trends?.map(m => ({
      month: m.month.substring(5), // "MM" from "YYYY-MM"
      orders: Number(m.orders) || 0,
      gmv: Number(m.gmv) || 0
    })) || []

    const mgVolumeData = stats?.orders_by_mg?.map(m => ({
      name: fmtMg(m.mg_code).replace(/ MG$/i, ''),
      volume: Number(m.total_volume) || 0,
      orders: Number(m.order_count) || 0
    })) || []

    const categoryData = stats?.category_distribution?.map((c, i) => ({
      name: c.category,
      value: Number(c.product_count) || 0
    })) || []

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingCart className="h-7 w-7 text-emerald-600" /> Katale Marketplace Management
            </h2>
            <p className="text-gray-600 mt-1">Order pipeline, product analytics, and seller performance</p>
          </div>
          <button onClick={fetchMarketplaceStats} className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium transition">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>

        {marketplaceLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse"><div className="h-5 bg-gray-200 rounded w-32 mb-3" /><div className="h-8 bg-gray-200 rounded w-20" /></div>)}
          </div>
        ) : stats ? (
          <>
            {/* Top Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard icon={ShoppingCart} label="Total Orders" value={stats.orders?.total_orders || 0} sub={`${stats.orders?.completed_orders || 0} completed`} color="blue" />
              <StatCard icon={DollarSign} label="Total GMV" value={formatShort(stats.orders?.total_gmv)} sub="Gross Merchandise Value" color="emerald" />
              <StatCard icon={Package} label="Active Products" value={stats.products?.active_products || 0} sub={`${stats.products?.total_products || 0} total`} color="amber" />
              <StatCard icon={Store} label="Sellers" value={stats.products?.total_sellers || 0} sub={`${stats.buyers?.total_buyers || 0} buyers`} color="rose" />
              <StatCard icon={AlertTriangle} label="Open Disputes" value={stats.disputes?.open_disputes || 0} sub={`${stats.disputes?.investigating || 0} investigating`} color="rose" onClick={() => setActiveView('complaints-refunds')} />
            </div>

            {/* Charts Row 1: Order Pipeline + Escrow Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <PieChartIcon className="h-5 w-5 text-blue-600" /> Order Pipeline
                </h3>
                {orderStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={orderStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={3} dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {orderStatusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-400 text-center py-16">No order data yet</p>
                )}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-emerald-600" /> Escrow Status
                </h3>
                {escrowData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={escrowData} cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={3} dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {['#3B82F6', '#10B981', '#EF4444', '#8B5CF6'].map((color, i) => <Cell key={i} fill={color} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-400 text-center py-16">No escrow data yet</p>
                )}
              </div>
            </div>

            {/* Charts Row 2: Monthly Trends */}
            {monthlyData.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" /> Monthly Trends (Last 6 Months)
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={monthlyData} margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => formatShort(v)} />
                    <Tooltip formatter={(value, name) => [name === 'gmv' ? formatCurrency(value) : value, name === 'gmv' ? 'GMV' : 'Orders']} />
                    <Legend />
                    <Area yAxisId="left" type="monotone" dataKey="orders" stroke="#3B82F6" fill="#3B82F680" name="Orders" />
                    <Area yAxisId="right" type="monotone" dataKey="gmv" stroke="#10B981" fill="#10B98140" name="GMV" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Charts Row 3: MG Volume + Category Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {mgVolumeData.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-blue-600" /> Top MGs by Volume
                  </h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={mgVolumeData} margin={{ top: 5, right: 20, left: 5, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 11 }} interval={0} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatShort(v)} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="volume" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="Volume" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {categoryData.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="h-5 w-5 text-amber-600" /> Products by Category
                  </h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={categoryData} cx="50%" cy="50%" outerRadius={90} paddingAngle={2} dataKey="value"
                        label={({ name, value }) => `${name} (${value})`}>
                        {categoryData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* MG Status Strip (compact) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-600" /> MG Network Status
              </h3>
              <div className="flex flex-wrap gap-2">
                {tenantData.filter(t => t.id !== 'demo').sort((a, b) => (b.customerCount || 0) - (a.customerCount || 0)).map(tenant => (
                  <div key={tenant.id} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${
                    tenant.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${tenant.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    {fmtMg(tenant.name)}
                    <span className="text-gray-500 ml-1">{(tenant.customerCount || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
            <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No marketplace data available yet</p>
            <p className="text-gray-400 text-sm mt-1">Data will appear once marketplace orders start coming in</p>
          </div>
        )}
      </div>
    )
  }

  // ==================== MEMBER SEARCH ====================
  const renderMemberSearch = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Global Member Search</h2>
        <p className="text-gray-600 mt-1">Search for any member across all MG databases</p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="memberSearch" className="block text-sm font-medium text-gray-700 mb-1.5">Member Number, Name, or Customer ID</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input id="memberSearch" type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} onKeyPress={handleKeyPress}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" placeholder="e.g., 10000011 or member name" disabled={loading} />
            </div>
          </div>
          <div className="flex gap-2 items-end">
            <button onClick={searchMember} disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition flex items-center gap-2 disabled:opacity-50">
              {loading ? <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> Searching...</> : <><Search className="h-5 w-5" /> Search All MGs</>}
            </button>
            {(memberData || searchResults.length > 0) && (
              <button onClick={clearSearch} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-3 rounded-lg font-medium transition flex items-center gap-2">
                <X className="h-5 w-5" /> Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {success && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg flex items-center gap-2"><CheckCircle className="h-5 w-5 shrink-0" /> {success}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2"><AlertCircle className="h-5 w-5 shrink-0" /> {error}</div>}

      {/* Multi results */}
      {searchResults.length > 0 && !memberData && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Users className="h-5 w-5" /> Select a Member ({searchResults.length} found)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MG / Tenant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-200">
                {searchResults.map((c, i) => (
                  <tr key={`${c.customerId}-${c.tenantDatabase || c.tenantDb}-${i}`} className="hover:bg-blue-50 transition">
                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{c.fullName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600 font-mono text-sm">{c.memberNumber || c.memberNo}</td>
                    <td className="px-6 py-4 whitespace-nowrap"><span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{fmtMg(c.tenantName || c.mgCode || 'Unknown')}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-sm">{formatDate(c.membershipDate)}</td>
                    <td className="px-6 py-4 whitespace-nowrap"><button onClick={() => selectMember(c)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition">Select</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Member Detail */}
      {memberData && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 backdrop-blur p-3 rounded-xl"><User className="h-8 w-8 text-white" /></div>
                <div className="text-white">
                  <h3 className="text-xl font-bold">{memberData.fullName}</h3>
                  <div className="flex items-center gap-3 mt-1 text-blue-100 text-sm">
                    <span className="font-mono">#{memberData.memberNo}</span>
                    <span>•</span>
                    <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs font-medium">{fmtMg(memberData.tenantName || memberData.mgCode)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              {memberData.customerId && <InfoField label="Customer ID" value={memberData.customerId} />}
              {memberData.phone && <InfoField label="Phone" value={memberData.phone} />}
              {memberData.email && <InfoField label="Email" value={memberData.email} />}
              {memberData.monthlyIncome > 0 && <InfoField label="Monthly Income" value={formatCurrency(memberData.monthlyIncome)} />}
              {memberData.membershipDate && <InfoField label="Member Since" value={formatDate(memberData.membershipDate)} />}
              {memberData.dateAdded && <InfoField label="Date Added" value={formatDate(memberData.dateAdded)} />}
              {memberData.companyId && <InfoField label="Company ID" value={memberData.companyId} />}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MemberStatCard icon={FileText} label="Financial Statements" value={financialStatements.length} color="blue" onClick={() => setActiveTab('financial')} />
            <MemberStatCard icon={CreditCard} label="Loan Applications" value={loans.length} color="amber" onClick={() => setActiveTab('loans')} />
            <MemberStatCard icon={TrendingUp} label="Shares Balance" value={shares ? formatShort(shares.currentBalance) : 'UGX 0'} color="emerald" onClick={() => setActiveTab('shares')} />
          </div>

          {/* Tab Content */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="border-b border-gray-200 px-6">
              <nav className="-mb-px flex space-x-6">
                {[
                  { key: 'overview', label: 'Overview', icon: TrendingUp },
                  { key: 'financial', label: `Statements (${financialStatements.length})`, icon: FileText },
                  { key: 'loans', label: `Loans (${loans.length})`, icon: CreditCard },
                  { key: 'shares', label: 'Shares', icon: TrendingUp }
                ].map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium transition ${activeTab === tab.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                    <tab.icon className="h-4 w-4" /> {tab.label}
                  </button>
                ))}
              </nav>
            </div>
            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 rounded-lg p-5">
                    <h4 className="font-semibold text-gray-900 mb-2">Financial Statements</h4>
                    <p className="text-3xl font-bold text-blue-600">{financialStatements.length}</p>
                    <p className="text-gray-500 text-sm mt-1">Total submissions</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5">
                    <h4 className="font-semibold text-gray-900 mb-2">Loan Applications</h4>
                    <p className="text-3xl font-bold text-amber-600">{loans.length}</p>
                    <p className="text-gray-500 text-sm mt-1">{loans.filter(l => ['approved', 'active', 'disbursed'].includes(l.loanStatus)).length} active</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5">
                    <h4 className="font-semibold text-gray-900 mb-2">Shares Balance</h4>
                    <p className="text-3xl font-bold text-emerald-600">{shares ? formatCurrency(shares.currentBalance) : formatCurrency(0)}</p>
                    <p className="text-gray-500 text-sm mt-1">{shares?.numberOfShares ? `${shares.numberOfShares.toLocaleString()} shares` : 'No shares'}</p>
                  </div>
                </div>
              )}
              {activeTab === 'financial' && (financialStatements.length === 0 ? <Empty icon={FileText} msg="No financial statements found" /> : (
                <div className="space-y-4">
                  {financialStatements.map((s, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-900 capitalize">{s.statement_type?.replace('_', ' ')} Statement</h4>
                          <p className="text-gray-600 text-sm">Period: {s.reporting_period}</p>
                          <p className="text-gray-500 text-xs mt-1">Submitted: {formatDate(s.created_at)}</p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s.statement_type === 'profit_loss' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                          {s.statement_type === 'profit_loss' ? 'P&L' : 'Balance Sheet'}
                        </span>
                      </div>
                      {s.products?.length > 0 && (
                        <div className="mt-3"><p className="text-xs font-medium text-gray-500 uppercase mb-2">Products</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {s.products.map((p, j) => <div key={j} className="bg-gray-50 p-2 rounded text-sm"><p className="font-medium">{p.product_name}</p><p className="text-gray-600">Rev: {formatCurrency(p.revenue)} | GP: {formatCurrency(p.gross_profit)}</p></div>)}
                          </div>
                        </div>
                      )}
                      {s.operating_expenses?.length > 0 && (
                        <div className="mt-3"><p className="text-xs font-medium text-gray-500 uppercase mb-2">Operating Expenses</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {s.operating_expenses.map((e, j) => <div key={j} className="bg-gray-50 p-2 rounded text-sm"><p className="font-medium">{e.expense_category}</p><p className="text-gray-600">{e.expense_description}: {formatCurrency(e.amount)}</p></div>)}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
              {activeTab === 'loans' && (loans.length === 0 ? <Empty icon={CreditCard} msg="No loan applications found" /> : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50"><tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purpose</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-200">
                      {loans.map((l, i) => (
                        <tr key={i} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono">{l.applicationId}</td>
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900">{formatCurrency(l.loanAmountApplied)}</td>
                          <td className="px-6 py-4"><StatusBadge status={l.loanStatus} /></td>
                          <td className="px-6 py-4 text-sm text-gray-600">{formatDate(l.applicationDate)}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{l.loanPurpose || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              {activeTab === 'shares' && (!shares ? <Empty icon={TrendingUp} msg="No share information found" /> : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-emerald-50 p-4 rounded-lg"><p className="text-sm text-emerald-600 font-medium">Current Balance</p><p className="text-2xl font-bold text-emerald-900 mt-1">{formatCurrency(shares.currentBalance)}</p></div>
                    <div className="bg-blue-50 p-4 rounded-lg"><p className="text-sm text-blue-600 font-medium">Number of Shares</p><p className="text-2xl font-bold text-blue-900 mt-1">{shares.numberOfShares?.toLocaleString() || '0'}</p></div>
                    <div className="bg-gray-50 p-4 rounded-lg"><p className="text-sm text-gray-600 font-medium">Last Updated</p><p className="text-lg font-bold text-gray-900 mt-1">{formatDate(shares.lastUpdated)}</p></div>
                  </div>
                  {shares.transactions?.length > 0 && (
                    <div><h4 className="font-semibold text-gray-900 mb-3">Recent Transactions</h4>
                      <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50"><tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shares</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-200">
                          {shares.transactions.slice(0, 10).map((t, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm">{formatDate(t.purchaseDate)}</td>
                              <td className="px-6 py-4 text-sm">{t.numberOfSharesBought?.toLocaleString() || '—'}</td>
                              <td className="px-6 py-4 text-sm">{formatCurrency(t.sharePrice)}</td>
                              <td className="px-6 py-4 text-sm font-semibold">{formatCurrency(t.totalAmount)}</td>
                              <td className="px-6 py-4 text-sm text-gray-600">{t.paymentMethod || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // ==================== CLEARING HOUSE ====================
  const renderClearingHouse = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Cross-MG Clearing House</h2>
          <p className="text-gray-600 mt-1">UFCC acts as clearing house for all cross-MG Katale transactions</p>
        </div>
        <button onClick={fetchClearingData} className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium transition">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {clearingLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
          <span className="ml-3 text-gray-600">Loading clearing data...</span>
        </div>
      ) : clearingError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" /> {clearingError}
          <button onClick={fetchClearingData} className="ml-4 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium">Retry</button>
        </div>
      ) : (
        <>
          {/* Clearing Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard icon={ArrowLeftRight} label="Total Batches" value={allBatches.length} color="blue" />
            <StatCard icon={Clock} label="Pending (MG)" value={allBatches.filter(b => b.status === 'pending').length} sub={formatShort(allBatches.filter(b => b.status === 'pending').reduce((s, b) => s + parseFloat(b.net_amount || 0), 0))} color="amber" />
            <StatCard icon={ShieldAlert} label="Awaiting Your Confirm" value={allBatches.filter(b => b.status === 'awaiting_confirmation').length} sub={formatShort(allBatches.filter(b => b.status === 'awaiting_confirmation').reduce((s, b) => s + parseFloat(b.net_amount || 0), 0))} color="rose" onClick={() => setClearingFilter('awaiting_confirmation')} />
            <StatCard icon={CheckCheck} label="Settled" value={allBatches.filter(b => b.status === 'settled').length} color="emerald" />
            <StatCard icon={AlertTriangle} label="Active Disputes" value={disputes.length} sub={disputes.length > 0 ? 'Needs mediation' : 'None'} color="rose" />
          </div>

          {/* Clearing House Role Info */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-700 rounded-xl p-5 text-white">
            <div className="flex items-start gap-3">
              <Shield className="h-6 w-6 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg">UFCC Clearing House Role</h3>
                <p className="text-blue-100 text-sm mt-1">
                  As the federation's clearing house, UFCC oversees all cross-MG settlements, ensures escrow protection for buyers, 
                  mediates disputes, and ensures fair settlement between MGs. Payment is only released to sellers after buyer confirmation of delivery.
                </p>
              </div>
            </div>
          </div>

          {/* Disputes Section */}
          {disputes.length > 0 && (
            <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-red-100 bg-red-50 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-red-900 flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-red-600" /> Active Disputes Requiring Mediation
                </h3>
                <span className="bg-red-200 text-red-800 text-sm font-medium px-3 py-1 rounded-full">{disputes.length}</span>
              </div>
              <div className="divide-y divide-gray-200">
                {disputes.map(dispute => (
                  <div key={dispute.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-900">Order #{dispute.order_id} — {dispute.reason?.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-gray-600 mt-1">{dispute.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span>Buyer: {dispute.buyer_mg_code}/{dispute.buyer_mem_no}</span>
                          <span>•</span>
                          <span>Seller: {dispute.seller_mg_code}/{dispute.seller_mem_no}</span>
                          <span>•</span>
                          <span>Filed: {formatDate(dispute.created_at)}</span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        dispute.status === 'open' ? 'bg-red-100 text-red-700' :
                        dispute.status === 'investigating' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }`}>{dispute.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settlement Batches Filter & List */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ArrowLeftRight className="h-5 w-5" /> All Cross-MG Settlement Batches
              </h3>
            </div>

            {/* Filter */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2 text-sm flex-wrap">
              <span className="text-gray-500">Filter:</span>
              {['all', 'pending', 'awaiting_confirmation', 'settled'].map(f => (
                <button key={f} onClick={() => setClearingFilter(f)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    clearingFilter === f ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}>
                  {f === 'awaiting_confirmation' ? 'Awaiting UFCC' : f.charAt(0).toUpperCase() + f.slice(1)}
                  {f !== 'all' && ` (${allBatches.filter(b => b.status === f).length})`}
                </button>
              ))}
            </div>

            {(() => {
              const filtered = clearingFilter === 'all' ? allBatches : allBatches.filter(b => b.status === clearingFilter)
              return filtered.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From MG</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">To MG</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Amount</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Txns</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Confirmations</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filtered.map(batch => (
                        <tr key={batch.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 text-sm">{batch.batch_number || `#${batch.id}`}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">{batch.from_mg_code}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">{batch.to_mg_code}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-bold text-gray-900">{formatCurrency(batch.net_amount)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">{batch.transaction_count || batch.order_count || 0}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              batch.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                              batch.status === 'awaiting_confirmation' ? 'bg-blue-100 text-blue-700' :
                              batch.status === 'settled' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>{batch.status === 'awaiting_confirmation' ? 'Awaiting UFCC' : batch.status}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className={`px-1.5 py-0.5 rounded text-xs ${batch.confirmed_by_from_mg ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                {batch.confirmed_by_from_mg ? '✓' : '○'} From
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${batch.confirmed_by_to_mg ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                {batch.confirmed_by_to_mg ? '✓' : '○'} To
                              </span>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${batch.confirmed_by_ufcc ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                                {batch.confirmed_by_ufcc ? '✓' : '○'} UFCC
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {batch.status === 'awaiting_confirmation' && (
                              <button onClick={() => setApprovingBatch(batch)}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1">
                                <ShieldCheck className="w-3 h-3" /> Confirm Settlement
                              </button>
                            )}
                            {batch.status === 'pending' && (
                              <button onClick={() => setApprovingBatch(batch)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1">
                                <ShieldCheck className="w-3 h-3" /> Override & Settle
                              </button>
                            )}
                            {batch.status === 'settled' && (
                              <span className="text-green-600 text-xs flex items-center gap-1"><CheckCheck className="w-3 h-3" /> Done</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <ArrowLeftRight className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <p className="font-medium">{clearingFilter === 'all' ? 'No cross-MG batches yet' : `No ${clearingFilter} batches`}</p>
                </div>
              )
            })()}
          </div>

          {/* How Clearing Works */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <h3 className="font-semibold text-blue-900 flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5" /> How the Clearing House Works
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                { step: 1, title: 'Order Placed', desc: 'Buyer pays from savings → funds held in escrow by UFCC' },
                { step: 2, title: 'Seller Ships', desc: 'Seller delivers goods and updates delivery status' },
                { step: 3, title: 'Buyer Confirms', desc: 'Buyer confirms receipt and quality → escrow released to settlement' },
                { step: 4, title: 'MG Admins Confirm', desc: 'Both the debtor and creditor MG admins confirm the settlement' },
                { step: 5, title: 'UFCC Settles', desc: 'UFCC SuperAdmin confirms and finalizes the cross-MG settlement' }
              ].map(item => (
                <div key={item.step} className="bg-white rounded-lg p-3">
                  <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                    <span className="text-blue-600 font-bold text-sm">{item.step}</span>
                  </div>
                  <h4 className="font-medium text-gray-900 text-sm mb-1">{item.title}</h4>
                  <p className="text-xs text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )

  // ==================== COMMISSIONS ====================
  const renderCommissions = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">UFCC Commissions</h2>
          <p className="text-gray-600 mt-1">MG Katale marketplace commission tracking</p>
        </div>
        <button onClick={fetchUfccCommissions} className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium transition">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {ufccLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
          <span className="ml-3 text-gray-600">Loading commission data...</span>
        </div>
      ) : ufccError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5" /> {ufccError}
          <button onClick={fetchUfccCommissions} className="ml-4 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium">Retry</button>
        </div>
      ) : ufccCommissions && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={DollarSign} label="Total Commission" value={formatCurrency(ufccCommissions.summary?.total_commission)} color="blue" />
            <StatCard icon={Clock} label="Pending from MGs" value={formatCurrency(ufccCommissions.summary?.total_pending)} color="amber" />
            <StatCard icon={Building2} label="Awaiting Confirmation" value={formatCurrency(ufccCommissions.summary?.total_settled)} color="blue" />
            <StatCard icon={CheckCheck} label="Confirmed Received" value={formatCurrency(ufccCommissions.summary?.total_confirmed)} color="emerald" />
          </div>

          {ufccCommissions.pending_settlements?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-amber-50 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-amber-900 flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" /> Settlements Awaiting Your Confirmation</h3>
                <span className="bg-amber-200 text-amber-800 text-sm font-medium px-3 py-1 rounded-full">{ufccCommissions.pending_settlements.length} pending</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50"><tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MG</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Ref</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Txns</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-200">
                    {ufccCommissions.pending_settlements.map(s => (
                      <tr key={s.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{s.mg_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-lg font-semibold text-gray-900">{formatCurrency(s.amount)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">{s.payment_reference}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">{s.payment_method?.replace('_', ' ')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{s.transaction_count}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(s.created_at)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button onClick={() => setConfirmingSettlement(s)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1.5">
                            <CheckCheck className="h-4 w-4" /> Confirm
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Building2 className="h-5 w-5" /> Commission by MG</h3>
              <p className="text-sm text-gray-500 mt-0.5">Period: {ufccCommissions.period?.start_date} to {ufccCommissions.period?.end_date}</p>
            </div>
            {ufccCommissions.mg_commissions?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50"><tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MG Name</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pending</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Settled</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Confirmed</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Txns</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-200">
                    {ufccCommissions.mg_commissions.map(mg => (
                      <tr key={mg.mg_key} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{mg.mg_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-semibold text-gray-900">{formatCurrency(mg.total_commission)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">{mg.pending_amount > 0 ? <span className="text-amber-600 font-medium">{formatCurrency(mg.pending_amount)}</span> : <span className="text-gray-400">—</span>}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">{mg.settled_amount > 0 ? <span className="text-cyan-600 font-medium">{formatCurrency(mg.settled_amount)}</span> : <span className="text-gray-400">—</span>}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">{mg.confirmed_amount > 0 ? <span className="text-emerald-600 font-medium">{formatCurrency(mg.confirmed_amount)}</span> : <span className="text-gray-400">—</span>}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-gray-600">{mg.total_transactions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <Empty icon={DollarSign} msg="No commission data available for this period" />}
          </div>
        </>
      )}
    </div>
  )

  // ==================== COMPLAINTS & REFUNDS RENDER ====================
  const renderComplaintsRefunds = () => {
    const openComplaints = allComplaints.filter(c => c.status === 'open' || c.status === 'investigating')
    const resolvedComplaints = allComplaints.filter(c => ['resolved_buyer', 'resolved_seller', 'closed'].includes(c.status))
    const filtered = complaintsFilter === 'all' ? allComplaints 
      : complaintsFilter === 'open' ? openComplaints 
      : resolvedComplaints

    const statusColor = (s) => {
      const m = { open: 'bg-red-100 text-red-700', investigating: 'bg-amber-100 text-amber-700', resolved_buyer: 'bg-green-100 text-green-700', resolved_seller: 'bg-blue-100 text-blue-700', closed: 'bg-gray-100 text-gray-700' }
      return m[s] || 'bg-gray-100 text-gray-700'
    }
    const escrowColor = (s) => {
      const m = { held: 'bg-amber-100 text-amber-700', released: 'bg-green-100 text-green-700', disputed: 'bg-red-100 text-red-700', refunded: 'bg-purple-100 text-purple-700' }
      return m[s] || 'bg-gray-100 text-gray-700'
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Complaints & Refunds</h2>
            <p className="text-sm text-gray-500 mt-1">Manage disputes, process refunds from escrow</p>
          </div>
          <button onClick={fetchComplaints} disabled={complaintsLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${complaintsLoading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {/* Alerts */}
        {complaintError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" /> {complaintError}
            <button onClick={() => setComplaintError('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}
        {complaintSuccess && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-700">
            <CheckCircle className="h-4 w-4 flex-shrink-0" /> {complaintSuccess}
            <button onClick={() => setComplaintSuccess('')} className="ml-auto"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-100"><AlertTriangle className="h-6 w-6 text-red-600" /></div>
            <div><p className="text-2xl font-bold text-gray-900">{openComplaints.length}</p><p className="text-sm text-gray-500">Open Disputes</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-100"><CheckCircle className="h-6 w-6 text-green-600" /></div>
            <div><p className="text-2xl font-bold text-gray-900">{resolvedComplaints.length}</p><p className="text-sm text-gray-500">Resolved</p></div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-100"><RotateCcw className="h-6 w-6 text-purple-600" /></div>
            <div><p className="text-2xl font-bold text-gray-900">{allComplaints.filter(c => c.status === 'resolved_buyer').length}</p><p className="text-sm text-gray-500">Refunds Issued</p></div>
          </div>
        </div>

        {/* === REFUND LOOKUP BY REFERENCE === */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Process Refund</h3>
          <p className="text-sm text-gray-500 mb-4">Customer must provide their order reference number (e.g. KTL-20260324-ABCDEF12) or debit reference. Refund is only possible if funds are still in escrow.</p>
          
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={refundSearch}
                onChange={(e) => setRefundSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && lookupOrderForRefund()}
                placeholder="Enter order number or debit reference..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button onClick={lookupOrderForRefund} disabled={refundLookupLoading || !refundSearch.trim()}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              {refundLookupLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Lookup
            </button>
          </div>

          {/* Order Lookup Result */}
          {refundOrder && (
            <div className="mt-5 border border-gray-200 rounded-xl p-5 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-lg font-bold text-gray-900">{refundOrder.order_number}</p>
                  <p className="text-sm text-gray-500">{refundOrder.buyer_name} ({refundOrder.buyer_mem_no} — {fmtMg(refundOrder.buyer_mg_code)})</p>
                </div>
                <div className="flex gap-2">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${escrowColor(refundOrder.escrow_status)}`}>
                    Escrow: {refundOrder.escrow_status}
                  </span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${refundOrder.status === 'refunded' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'}`}>
                    {refundOrder.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <div><p className="text-xs text-gray-500">Subtotal</p><p className="font-semibold">{formatCurrency(refundOrder.subtotal)}</p></div>
                <div><p className="text-xs text-gray-500">Delivery</p><p className="font-semibold">{formatCurrency(refundOrder.delivery_fee)}</p></div>
                <div><p className="text-xs text-gray-500">Commission</p><p className="font-semibold">{formatCurrency(refundOrder.ufcc_commission)}</p></div>
                <div><p className="text-xs text-gray-500">Total</p><p className="font-bold text-blue-600">{formatCurrency(refundOrder.total_amount)}</p></div>
              </div>

              <div className="text-sm">
                <p className="text-gray-500">Products: <span className="text-gray-900">{refundOrder.product_names || 'N/A'}</span></p>
                <p className="text-gray-500">Items: {refundOrder.item_count} &middot; Ordered: {formatDate(refundOrder.created_at)}</p>
                {refundOrder.buyer_debit_ref && <p className="text-gray-500">Debit Ref: <span className="font-mono text-gray-900">{refundOrder.buyer_debit_ref}</span></p>}
              </div>

              {/* Existing disputes on this order */}
              {refundOrder.disputes && refundOrder.disputes.length > 0 && (
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-amber-800 mb-1">Disputes on this order:</p>
                  {refundOrder.disputes.map((d, i) => (
                    <div key={i} className="text-xs text-amber-700">
                      <span className="font-medium">{d.reason.replace(/_/g, ' ')}</span> — {d.status} {d.resolved_at ? `(resolved ${formatDate(d.resolved_at)})` : `(opened ${formatDate(d.created_at)})`}
                      {d.description && <p className="text-amber-600 ml-4">"{d.description}"</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Refund Action */}
              {refundOrder.refundable ? (
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                    <p className="font-medium">✅ Eligible for refund</p>
                    <p>Funds ({formatCurrency(refundOrder.total_amount)}) are still in escrow and can be returned to the buyer's voluntary savings account.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason for refund *</label>
                    <textarea
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Describe why this refund is being issued..."
                      className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={handleProcessRefund} disabled={processingRefund || !refundReason.trim()}
                      className="px-5 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
                      {processingRefund ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                      Process Refund ({formatCurrency(refundOrder.total_amount)})
                    </button>
                    <button onClick={() => { setRefundOrder(null); setRefundReason('') }}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                  <p className="font-medium">❌ Not eligible for refund</p>
                  <p>Escrow is <strong>{refundOrder.escrow_status}</strong>. Funds can only be refunded if escrow status is "held" or "disputed".</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* === DISPUTES LIST === */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-5 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="text-lg font-semibold text-gray-900">All Disputes</h3>
            <div className="flex gap-2">
              {['all', 'open', 'resolved'].map(f => (
                <button key={f} onClick={() => setComplaintsFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${complaintsFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {f === 'all' ? `All (${allComplaints.length})` : f === 'open' ? `Open (${openComplaints.length})` : `Resolved (${resolvedComplaints.length})`}
                </button>
              ))}
            </div>
          </div>

          {complaintsLoading ? (
            <div className="p-12 flex justify-center"><RefreshCw className="h-8 w-8 animate-spin text-gray-300" /></div>
          ) : filtered.length === 0 ? (
            <Empty icon={AlertTriangle} msg="No disputes found" />
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((dispute) => (
                <div key={dispute.id} className="p-5 hover:bg-gray-50 transition">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">#{dispute.order_id}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(dispute.status)}`}>
                          {dispute.status?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-400">{dispute.reason?.replace(/_/g, ' ')}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 truncate">{dispute.description || 'No description'}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        <span>Buyer: {dispute.buyer_mem_no} ({fmtMg(dispute.buyer_mg_code)})</span>
                        {dispute.seller_mem_no && <span>Seller: {dispute.seller_mem_no} ({fmtMg(dispute.seller_mg_code)})</span>}
                        <span>{formatDate(dispute.created_at)}</span>
                      </div>
                      {dispute.resolution_notes && (
                        <p className="text-xs text-gray-500 mt-2 bg-gray-50 p-2 rounded"><strong>Resolution:</strong> {dispute.resolution_notes}</p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {(dispute.status === 'open' || dispute.status === 'investigating') && (
                        <button onClick={() => { setResolvingDispute(dispute); setResolveResolution('resolved_buyer'); setResolveNotes('') }}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resolve Dispute Modal */}
        {resolvingDispute && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Resolve Dispute #{resolvingDispute.id}</h3>
                <button onClick={() => setResolvingDispute(null)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <p><strong>Reason:</strong> {resolvingDispute.reason?.replace(/_/g, ' ')}</p>
                  <p><strong>Description:</strong> {resolvingDispute.description || 'None'}</p>
                  <p className="text-gray-500 mt-1">Order #{resolvingDispute.order_id} &middot; Buyer: {resolvingDispute.buyer_mem_no}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resolution</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{v: 'resolved_buyer', l: 'Favour Buyer', c: 'border-green-500 bg-green-50 text-green-700'},
                      {v: 'resolved_seller', l: 'Favour Seller', c: 'border-blue-500 bg-blue-50 text-blue-700'},
                      {v: 'closed', l: 'Close', c: 'border-gray-500 bg-gray-50 text-gray-700'}].map(opt => (
                      <button key={opt.v} onClick={() => setResolveResolution(opt.v)}
                        className={`p-2 rounded-lg text-xs font-medium border-2 transition ${
                          resolveResolution === opt.v ? opt.c : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                        }`}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                  {resolveResolution === 'resolved_buyer' && (
                    <p className="text-xs text-green-600 mt-1">Buyer wins — you can then process a refund from the lookup tool above.</p>
                  )}
                  {resolveResolution === 'resolved_seller' && (
                    <p className="text-xs text-blue-600 mt-1">Seller wins — escrow will be released to seller.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resolution Notes *</label>
                  <textarea
                    value={resolveNotes}
                    onChange={(e) => setResolveNotes(e.target.value)}
                    placeholder="Explain the resolution decision..."
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <button onClick={() => setResolvingDispute(null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                    Cancel
                  </button>
                  <button onClick={handleResolveDispute} disabled={!resolveNotes.trim()}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                    Confirm Resolution
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ==================== MAIN RENDER ====================
  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
            <img src="/images/UFCC_Logo.png" alt="UFCC" className="h-10 w-10 rounded-lg shadow-lg object-contain" />
            <div className="flex-1 min-w-0">
              <h1 className="text-white font-bold text-sm leading-tight">UFCC Super Admin</h1>
              <p className="text-slate-400 text-xs truncate">Federation Console</p>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 text-slate-400 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Nav Items */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map(item => {
              const isActive = activeView === item.key
              return (
                <button
                  key={item.key}
                  onClick={() => { setActiveView(item.key); setSidebarOpen(false) }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive 
                      ? 'bg-blue-600/20 text-blue-400 shadow-sm' 
                      : 'text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <item.icon className={`h-4.5 w-4.5 shrink-0 ${isActive ? 'text-blue-400' : 'text-slate-400'}`} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      item.badgeColor === 'warning' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              )
            })}
          </nav>

          {/* User & Logout */}
          <div className="px-3 py-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              <div className="w-8 h-8 bg-blue-600/30 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {superAdmin?.fullName || superAdmin?.full_name || 'Super Admin'}
                </p>
                <p className="text-xs text-slate-400 truncate">{superAdmin?.email || 'super_admin'}</p>
              </div>
            </div>
            <button 
              onClick={logout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-red-600/10 hover:text-red-400 transition"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile menu toggle + breadcrumb) */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between h-14 px-4 sm:px-6">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <Menu className="h-5 w-5" />
              </button>
              <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500">
                <img src="/images/UFCC_Logo.png" alt="UFCC" className="h-5 w-5 object-contain" />
                <span className="text-gray-300">/</span>
                <span className="font-medium text-gray-900">
                  {navItems.find(n => n.key === activeView)?.label || 'Dashboard'}
                </span>
              </div>
              {/* Mobile: show current page title */}
              <span className="sm:hidden text-sm font-semibold text-gray-900">
                {navItems.find(n => n.key === activeView)?.label || 'Dashboard'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => { fetchDashboardData(); fetchUfccCommissions() }} 
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
                title="Refresh data"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
              <span className="hidden md:block text-xs text-gray-400">
                {new Date().toLocaleDateString('en-UG', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {activeView === 'dashboard' && renderDashboard()}
            {activeView === 'katale-management' && renderKataleManagement()}
            {activeView === 'member-search' && renderMemberSearch()}
            {activeView === 'clearing-house' && renderClearingHouse()}
            {activeView === 'ufcc-commissions' && renderCommissions()}
            {activeView === 'complaints-refunds' && renderComplaintsRefunds()}
          </div>
        </main>
      </div>

      {/* Clearing House Approval Modal */}
      {approvingBatch && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
                {approvingBatch.status === 'awaiting_confirmation' ? 'Confirm Cross-MG Settlement' : 'Override & Settle Batch'}
              </h3>
              <p className="text-sm text-gray-500 mb-2">Batch {approvingBatch.batch_number || `#${approvingBatch.id}`}</p>
              {approvingBatch.status === 'awaiting_confirmation' ? (
                <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4 flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Both MG admins have confirmed this settlement. As UFCC clearing house, confirm to finalize.
                </p>
              ) : (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  This batch has not been confirmed by both MGs yet. You are overriding the normal flow.
                </p>
              )}
              <div className="space-y-3 bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">From MG:</span>
                  <span className="font-medium text-red-600">{approvingBatch.from_mg_code}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">To MG:</span>
                  <span className="font-medium text-green-600">{approvingBatch.to_mg_code}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-2">
                  <span className="text-gray-600">Net Amount:</span>
                  <span className="font-bold text-gray-900">{formatCurrency(approvingBatch.net_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Confirmations:</span>
                  <div className="flex gap-1">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${approvingBatch.confirmed_by_from_mg ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {approvingBatch.confirmed_by_from_mg ? '✓' : '✗'} From
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${approvingBatch.confirmed_by_to_mg ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {approvingBatch.confirmed_by_to_mg ? '✓' : '✗'} To
                    </span>
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Confirmation Reference (optional)</label>
                <input type="text" value={confirmationRef} onChange={e => setConfirmationRef(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3"
                  placeholder="e.g., Bank receipt or internal reference" />
                <label className="text-sm font-medium text-gray-700 mb-1 block">Notes (optional)</label>
                <textarea value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={3} placeholder="Add any notes for this settlement confirmation..." />
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setApprovingBatch(null); setApprovalNotes(''); setConfirmationRef('') }}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-lg text-sm font-medium transition">
                  Cancel
                </button>
                <button onClick={() => handleApproveBatch(approvingBatch)}
                  className={`flex-1 ${approvingBatch.status === 'awaiting_confirmation' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'} text-white py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-2`}>
                  <ShieldCheck className="w-4 h-4" /> {approvingBatch.status === 'awaiting_confirmation' ? 'Confirm & Settle' : 'Override & Settle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmingSettlement && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Confirm Receipt of Payment</h3>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between"><span className="text-sm text-gray-500">MG</span><span className="font-semibold">{confirmingSettlement.mg_name}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-gray-500">Amount</span><span className="text-xl font-bold text-blue-600">{formatCurrency(confirmingSettlement.amount)}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-gray-500">Their Ref</span><span className="font-medium font-mono text-sm">{confirmingSettlement.payment_reference}</span></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Your Confirmation Reference *</label>
                  <input type="text" value={confirmationRef} onChange={(e) => setConfirmationRef(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Bank receipt number" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (Optional)</label>
                  <textarea value={confirmationNotes} onChange={(e) => setConfirmationNotes(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" rows={2} placeholder="Any additional notes..." />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl">
              <button onClick={() => { setConfirmingSettlement(null); setConfirmationRef(''); setConfirmationNotes('') }}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm transition">Cancel</button>
              <button onClick={() => handleConfirmSettlement(confirmingSettlement)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium text-sm transition flex items-center gap-2">
                <CheckCheck className="h-4 w-4" /> Confirm Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ==================== SUB-COMPONENTS ====================
const StatCard = ({ icon: Icon, label, value, sub, color = 'blue', onClick }) => {
  const c = { blue: 'bg-blue-100 text-blue-600', emerald: 'bg-emerald-100 text-emerald-600', amber: 'bg-amber-100 text-amber-600', rose: 'bg-rose-100 text-rose-600' }
  return (
    <div onClick={onClick} className={`bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition ${onClick ? 'cursor-pointer' : ''}`}>
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${c[color]}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  )
}

const MemberStatCard = ({ icon: Icon, label, value, color, onClick }) => {
  const c = { blue: 'bg-blue-100 text-blue-600', amber: 'bg-amber-100 text-amber-600', emerald: 'bg-emerald-100 text-emerald-600' }
  return (
    <div onClick={onClick} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition cursor-pointer">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${c[color]}`}><Icon className="h-5 w-5" /></div>
        <div><p className="text-sm text-gray-500">{label}</p><p className="text-2xl font-bold text-gray-900">{value}</p></div>
      </div>
    </div>
  )
}

const QuickAction = ({ icon: Icon, label, desc, onClick, highlight }) => (
  <button onClick={onClick}
    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition ${highlight ? 'bg-amber-50 hover:bg-amber-100 border border-amber-200' : 'bg-gray-50 hover:bg-gray-100'}`}>
    <div className={`p-2 rounded-lg ${highlight ? 'bg-amber-200' : 'bg-white border border-gray-200'}`}>
      <Icon className={`h-4 w-4 ${highlight ? 'text-amber-700' : 'text-gray-600'}`} />
    </div>
    <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-900">{label}</p><p className="text-xs text-gray-500 truncate">{desc}</p></div>
    <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
  </button>
)

const SummaryBox = ({ icon: Icon, value, label, color }) => {
  const c = { blue: 'bg-blue-100 text-blue-600', emerald: 'bg-emerald-100 text-emerald-600', red: 'bg-red-100 text-red-600' }
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
      <div className={`p-3 rounded-xl ${c[color]}`}><Icon className="h-6 w-6" /></div>
      <div><p className="text-2xl font-bold text-gray-900">{value}</p><p className="text-sm text-gray-500">{label}</p></div>
    </div>
  )
}

const MiniStat = ({ label, value, bg }) => {
  const c = { blue: 'bg-blue-50 text-blue-600 text-blue-900', amber: 'bg-amber-50 text-amber-600 text-amber-900', cyan: 'bg-cyan-50 text-cyan-600 text-cyan-900', emerald: 'bg-emerald-50 text-emerald-600 text-emerald-900' }
  const [bgClass] = (c[bg] || c.blue).split(' ')
  return (
    <div className={`${bgClass} rounded-lg p-4`}>
      <p className={`text-sm font-medium ${bg === 'blue' ? 'text-blue-600' : bg === 'amber' ? 'text-amber-600' : bg === 'cyan' ? 'text-cyan-600' : 'text-emerald-600'}`}>{label}</p>
      <p className={`text-xl font-bold mt-1 ${bg === 'blue' ? 'text-blue-900' : bg === 'amber' ? 'text-amber-900' : bg === 'cyan' ? 'text-cyan-900' : 'text-emerald-900'}`}>{value}</p>
    </div>
  )
}

const InfoField = ({ label, value }) => (
  <div><p className="text-xs text-gray-500 font-medium uppercase">{label}</p><p className="text-sm font-semibold text-gray-900 mt-0.5">{value}</p></div>
)

const StatusBadge = ({ status }) => {
  const s = { approved: 'bg-green-100 text-green-800', active: 'bg-green-100 text-green-800', disbursed: 'bg-green-100 text-green-800', pending: 'bg-yellow-100 text-yellow-800', rejected: 'bg-red-100 text-red-800' }
  return <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${s[status] || 'bg-gray-100 text-gray-800'}`}>{status}</span>
}

const Empty = ({ icon: Icon, msg }) => (
  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
    <Icon className="h-12 w-12 mb-3" /><p className="text-gray-500">{msg}</p>
  </div>
)

export default SuperAdminDashboard
